import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Input, Modal, Popconfirm, Select, Space, Table, Tag, Typography, notification } from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, SearchOutlined, SwapOutlined } from '@ant-design/icons';
import pmApi from './pmApi';

const { Text } = Typography;

/**
 * Benefit Item page: the global catalog of Benefit Items, created and
 * configured here only (name, request category, applicable check-in types),
 * with the number of tariff items mapped to each. Opening an item lists every
 * mapped tariff item name (each once) with a single action — Move — because
 * removing a mapping would break historical consumption. A Benefit Item can
 * be deleted only once nothing is mapped to it; deleting retires it from every
 * plan design and from plan configuration.
 */
export default function MappingsTab({ meta, onCatalogChanged }) {
  const [items, setItems] = useState([]);      // registry rows: benefit item + mappings
  const [catalog, setCatalog] = useState([]);  // catalog rows: status, check-in types, categories
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [q, setQ] = useState('');
  const [move, setMove] = useState(null);      // { mapping, targetId }
  const [moving, setMoving] = useState(false);
  const [editor, setEditor] = useState(null);  // { id?, name, costable_category_ids, checkin_type_ids }
  const [saving, setSaving] = useState(false);

  const categoryName = useCallback((id) => (meta?.categories || []).find((c) => Number(c.id) === Number(id))?.category_name || `category #${id}`, [meta]);
  const typeName = useCallback((id) => (meta?.checkin_types || []).find((t) => Number(t.checkin_type_id) === Number(id))?.checkin_type_name || `type ${id}`, [meta]);
  const idList = (v) => (Array.isArray(v) ? v : String(v || '').split(',')).map((x) => Number(x)).filter((x) => x > 0);

  const load = useCallback(async (benefitItemId) => {
    setLoading(true);
    const [reg, cat] = await Promise.all([
      pmApi.mappingRegistry(benefitItemId ? { benefit_item_id: benefitItemId } : {}),
      pmApi.benefitItems(),
    ]);
    setItems(reg?.data?.items || []);
    setCatalog(cat?.data?.benefit_items || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(selectedId); }, [load, selectedId]);

  const catalogById = useMemo(() => {
    const m = {};
    catalog.forEach((c) => { m[Number(c.id)] = c; });
    return m;
  }, [catalog]);

  // Every active Benefit Item, immediately, with its mapped-item count.
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items
      .filter((it) => Number(catalogById[Number(it.benefit_item_id)]?.status ?? 1) === 1)
      .map((it) => ({ ...it, cat: catalogById[Number(it.benefit_item_id)] || null, mapped: it.mappings.length }))
      .filter((it) => !needle || it.benefit_item.toLowerCase().includes(needle) || it.mappings.some((m) => String(m.name).toLowerCase().includes(needle)))
      .sort((a, b) => (b.mapped - a.mapped) || a.benefit_item.localeCompare(b.benefit_item));
  }, [items, catalogById, q]);

  const selected = useMemo(() => items.find((it) => Number(it.benefit_item_id) === Number(selectedId)) || null, [items, selectedId]);
  const targetOptions = useMemo(() => items
    .filter((it) => Number(catalogById[Number(it.benefit_item_id)]?.status ?? 1) === 1 && Number(it.benefit_item_id) !== Number(selectedId))
    .map((it) => ({ value: it.benefit_item_id, label: `${it.benefit_item} · ${categoryName(it.costable_category_id)}`, search: `${it.benefit_item} ${categoryName(it.costable_category_id)}`.toLowerCase() })), [items, catalogById, selectedId, categoryName]);

  const mappedRows = useMemo(() => {
    if (!selected) return [];
    const needle = q.trim().toLowerCase();
    const seen = new Set();
    return selected.mappings
      .filter((m) => { const k = String(m.name || '').trim().toLowerCase(); if (seen.has(k)) return false; seen.add(k); return !needle || k.includes(needle); })
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [selected, q]);

  const confirmMove = async () => {
    if (!move?.targetId || !move?.mapping) return;
    setMoving(true);
    const res = await pmApi.mapNames(move.targetId, [move.mapping.name], 'registry');
    setMoving(false);
    if (!res.ok) return;
    const target = items.find((it) => Number(it.benefit_item_id) === Number(move.targetId));
    const backfilled = Number(res?.data?.lines_backfilled || 0);
    notification.success({
      message: `"${move.mapping.name}" now maps to ${target?.benefit_item || 'the selected Benefit Item'}`,
      description: backfilled
        ? `${backfilled} existing line${backfilled === 1 ? '' : 's'} re-stamped across PA, claims processing and approved claims. Client consumption, limits and remaining balances now read from the new mapping.`
        : 'No existing lines carried this name; future submissions resolve to the new Benefit Item.',
      placement: 'topLeft',
      duration: 8,
    });
    setMove(null);
    load(selectedId);
  };

  const openNew = () => setEditor({ name: '', costable_category_ids: [], checkin_type_ids: [] });
  const openEdit = (row) => setEditor({
    id: row.benefit_item_id,
    name: row.benefit_item,
    costable_category_ids: idList(row.cat?.costable_category_ids).length ? idList(row.cat?.costable_category_ids) : [Number(row.costable_category_id)],
    checkin_type_ids: idList(row.cat?.checkin_type_ids),
  });
  const saveEditor = async () => {
    if (!editor?.name?.trim() || !editor.costable_category_ids.length) {
      notification.warning({ message: 'Name and a request category are required' });
      return;
    }
    setSaving(true);
    const body = { name: editor.name.trim(), costable_category_ids: editor.costable_category_ids, checkin_type_ids: editor.checkin_type_ids };
    const res = editor.id ? await pmApi.updateBenefitItem(editor.id, body) : await pmApi.createBenefitItem(body);
    setSaving(false);
    if (!res.ok) return;
    notification.success({ message: editor.id ? 'Benefit item updated' : 'Benefit item created', description: editor.id ? undefined : 'It is now available for selection in every plan design.' });
    setEditor(null);
    load(selectedId);
    onCatalogChanged?.();
  };

  const remove = async (row) => {
    const res = await pmApi.deleteBenefitItem(row.benefit_item_id);
    if (!res.ok) return;
    notification.success({ message: res.message || 'Benefit item deleted' });
    load(selectedId);
    onCatalogChanged?.();
  };
  const explainBlockedDelete = (row) => Modal.info({
    title: `${row.benefit_item} cannot be deleted yet`,
    content: `This Benefit Item has ${row.mapped} mapped tariff item${row.mapped === 1 ? '' : 's'}. Move the mapped items to another Benefit Item before deleting it.`,
    okText: 'Open mappings',
    onOk: () => { setSelectedId(row.benefit_item_id); setQ(''); },
  });

  const editorModal = (
    <Modal
      open={!!editor}
      title={editor?.id ? 'Edit Benefit Item' : 'New Benefit Item'}
      onCancel={() => setEditor(null)}
      onOk={saveEditor}
      okText={editor?.id ? 'Save' : 'Create'}
      okButtonProps={{ loading: saving }}
      destroyOnClose
    >
      {editor ? (
        <Space direction="vertical" style={{ width: '100%' }} size={10}>
          <div>
            <Text type="secondary">Benefit Item name</Text>
            <Input autoFocus placeholder="e.g. Specialist Consultation" value={editor.name} onChange={(e) => setEditor((s) => ({ ...s, name: e.target.value }))} />
          </div>
          <div>
            <Text type="secondary">Request category (first is the primary)</Text>
            <Select mode="multiple" style={{ width: '100%' }} placeholder="Request category" value={editor.costable_category_ids} onChange={(v) => setEditor((s) => ({ ...s, costable_category_ids: v }))} options={(meta?.categories || []).map((c) => ({ value: c.id, label: c.category_name }))} />
          </div>
          <div>
            <Text type="secondary">Applicable check-in type(s)</Text>
            <Select mode="multiple" style={{ width: '100%' }} placeholder="Check-in types" value={editor.checkin_type_ids} onChange={(v) => setEditor((s) => ({ ...s, checkin_type_ids: v }))} options={(meta?.checkin_types || []).map((t) => ({ value: t.checkin_type_id, label: t.checkin_type_name }))} />
          </div>
          <Text type="secondary">A Benefit Item is a global system item: create it once here and reuse it across plans, where each plan sets its own limit.</Text>
        </Space>
      ) : null}
    </Modal>
  );

  if (selected) {
    return (
      <div>
        <Space style={{ marginBottom: 12 }} wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={() => { setSelectedId(null); setQ(''); }}>All Benefit Items</Button>
          <Text strong style={{ fontSize: 15 }}>{selected.benefit_item}</Text>
          <Text type="secondary">
            {categoryName(selected.costable_category_id)}
            {selected.lines_resolved ? ` · ${selected.lines_resolved} line${selected.lines_resolved === 1 ? '' : 's'} currently resolve to it` : ''}
          </Text>
          <Input allowClear prefix={<SearchOutlined />} placeholder="Filter mapped items" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 240 }} />
          <Button icon={<ReloadOutlined />} loading={loading} onClick={() => load(selectedId)}>Refresh</Button>
        </Space>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="A mapped item can be moved, not removed"
          description="Moving a tariff item re-stamps every line carrying that name — at PA, in claims processing or already approved — so the client's consumption, the applicable limit and the remaining balance follow immediately. Removing a mapping is not offered because it would break historical consumption records."
        />
        <Table
          size="small"
          rowKey="mapping_id"
          loading={loading}
          dataSource={mappedRows}
          locale={{ emptyText: 'No tariff items are mapped to this Benefit Item.' }}
          pagination={mappedRows.length > 25 ? { pageSize: 25, showSizeChanger: true } : false}
          columns={[
            {
              title: 'Facility tariff item',
              dataIndex: 'name',
              key: 'name',
              render: (name, m) => (
                <div>
                  <Text>{name}</Text>
                  <div style={{ fontSize: 11, color: '#888' }}>mapped {m.source ? `via ${m.source}` : ''}{m.created_at ? ` · ${String(m.created_at).slice(0, 10)}` : ''}</div>
                </div>
              ),
            },
            { title: 'Lines', key: 'lines', width: 110, align: 'right', render: (_, m) => (m.lines === null || m.lines === undefined ? <Text type="secondary">—</Text> : Number(m.lines).toLocaleString()) },
            { title: '', key: 'actions', width: 140, render: (_, m) => <Button size="small" icon={<SwapOutlined />} onClick={() => setMove({ mapping: m, targetId: null })}>Move to…</Button> },
          ]}
        />
        <Modal
          open={!!move}
          title={move ? <span>Move "{move.mapping.name}"</span> : null}
          onCancel={() => setMove(null)}
          onOk={confirmMove}
          okText="Move and recalculate"
          okButtonProps={{ disabled: !move?.targetId, loading: moving }}
          destroyOnClose
        >
          {move ? (
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>Currently <Tag>{selected.benefit_item}</Tag> → move to</div>
              <Select showSearch autoFocus placeholder="Choose the new Benefit Item" style={{ width: '100%' }} value={move.targetId} onChange={(v) => setMove((mv) => ({ ...mv, targetId: v }))} options={targetOptions} filterOption={(input, opt) => (opt?.search || '').includes(input.toLowerCase())} />
              <Text type="secondary">
                Every line carrying this name{move.mapping.lines ? ` (${Number(move.mapping.lines).toLocaleString()} today)` : ''} is re-stamped at once, whatever its stage. Consumption leaves {selected.benefit_item} and lands on the new item; limits and remaining balances are recalculated.
              </Text>
            </Space>
          ) : null}
        </Modal>
      </div>
    );
  }

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Button type="primary" icon={<PlusOutlined />} onClick={openNew}>Add Benefit Item</Button>
        <Input allowClear prefix={<SearchOutlined />} placeholder="Search Benefit Items or mapped tariff items" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 320 }} />
        <Button icon={<ReloadOutlined />} loading={loading} onClick={() => load(null)}>Refresh</Button>
        <Text type="secondary">{rows.length} Benefit Item{rows.length === 1 ? '' : 's'} · {rows.reduce((n, r) => n + r.mapped, 0)} mapped tariff items</Text>
      </Space>
      <Table
        size="small"
        rowKey="benefit_item_id"
        loading={loading}
        dataSource={rows}
        pagination={rows.length > 25 ? { pageSize: 25, showSizeChanger: true } : false}
        onRow={(row) => ({ onClick: () => { setSelectedId(row.benefit_item_id); setQ(''); }, style: { cursor: 'pointer' } })}
        columns={[
          {
            title: 'Benefit Item',
            key: 'item',
            render: (_, row) => (
              <div>
                <Text strong>{row.benefit_item}</Text>
                <div style={{ fontSize: 11, color: '#888' }}>{categoryName(row.costable_category_id)}</div>
              </div>
            ),
          },
          {
            title: 'Check-in types',
            key: 'types',
            render: (_, row) => {
              const ids = idList(row.cat?.checkin_type_ids);
              return ids.length ? <Space size={[4, 4]} wrap>{ids.map((id) => <Tag key={id}>{typeName(id)}</Tag>)}</Space> : <Text type="secondary">any</Text>;
            },
          },
          { title: 'Mapped items', dataIndex: 'mapped', key: 'mapped', width: 130, align: 'right', render: (n) => <Text strong>{Number(n).toLocaleString()}</Text> },
          { title: 'Lines resolved', dataIndex: 'lines_resolved', key: 'lines', width: 130, align: 'right', render: (n) => Number(n || 0).toLocaleString() },
          {
            title: '',
            key: 'actions',
            width: 260,
            render: (_, row) => (
              <Space onClick={(e) => e.stopPropagation()}>
                <Button size="small" onClick={() => { setSelectedId(row.benefit_item_id); setQ(''); }}>Mappings</Button>
                <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)}>Edit</Button>
                {row.mapped > 0 ? (
                  <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => explainBlockedDelete(row)}>Delete</Button>
                ) : (
                  <Popconfirm
                    title={`Delete ${row.benefit_item}?`}
                    description="It is removed from every plan design and can no longer be selected when configuring a plan."
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => remove(row)}
                  >
                    <Button size="small" type="text" danger icon={<DeleteOutlined />}>Delete</Button>
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]}
      />
      {editorModal}
    </div>
  );
}
